import { useCallback, useRef, useState, type ReactElement } from 'react';
import {
  SearchShell,
  HeaderSearch,
  UserDetailCard,
  DeliveryLayout,
  LocationDeliveriesLayout,
  useSearchTabs,
} from '@citibox-panel/lockers-care';
import { Alert, Box, Stack, Text } from '@citibox-panel/ui';
import { UserMenu } from '@/features/user-menu';
import { defaultAudience } from '@/lib/defaultAudience';
import { legacyBoxMaintenanceUrl } from '@/lib/legacyBoxMaintenanceUrl';
import { resolveMarketIndicator } from '@/lib/marketIndicator';
import { readRuntimeConfig } from '@/lib/runtimeConfig';
import { useSession } from '@/lib/session';
import {
  fetchUsersByPhone,
  fetchUserDevice,
  fetchUserDeliveries,
  deleteUserAccount,
  updateUserAccount,
  blockUserAccount,
  unblockUserAccount,
  fetchDeliveryDetail,
  confirmDeliveryCollect,
  enableDelivery,
  nonCollectDelivery,
  rejectDelivery,
  retrieveDelivery,
  nonRetrieveDelivery,
  extendCustody,
  changeDeliveryRecipient,
  fetchLockerLog,
  fetchBoxStatus,
  fetchBoxMarks,
  clearBoxMarks,
  fetchLockCodes,
  fetchLocations,
  fetchLocationCarriers,
  fetchDeliveryStates,
  searchDeliveriesByTracking,
  fetchLocationDeliveries,
  confirmLockSequence,
  reportAddressMismatch,
  checkAddressMismatchReport,
} from '@/lib/apiClient';

// The authenticated home is the search-first shell. UserMenu and the header
// search input are injected into the shell's header slots so the shell module
// stays free of any apps/shell dependency. The shell's ⌘K / "+" / empty-state
// affordances focus the search input through onNewSearch.
export function HomeView(): ReactElement {
  const { roleNames } = useSession();
  const searchInputRef = useRef<HTMLInputElement>(null);
  // The screen's ONE announcement region lives here (CBX-11386). It was the header search's until
  // the «Usuario» ficha needed to announce a save too, and from inside the header nothing else on
  // the screen could reach it. A region has to PRE-EXIST its own text to be heard, so it cannot
  // belong to anything that mounts and unmounts while the agent works — and the header is exactly
  // that: it swaps a whole mode out every time the search type changes. An emitter words its own
  // sentence and is handed `onAnnounce`; the host knows where the text lands and nothing about
  // what it says.
  const [announced, setAnnounced] = useState('');
  // Announced because the region's content CHANGED, never because it was assigned: writing the
  // same sentence twice leaves the DOM untouched and assistive tech says nothing the second time.
  // «Datos actualizados» is a constant, so two corrections in a row collide every time — and the
  // second one is heard with the form already closed, which is when the announcement is the only
  // evidence left that the write happened.
  //
  // A trailing space, alternated, is the cheapest difference that exists: invisible on screen,
  // ignored by assistive tech, and DURABLE — unlike blanking the region and rewriting it, which is
  // two updates in one tick and so a single React commit straight back to the same text. Handed
  // down to both emitters, which go on wording their own sentence and knowing nothing about this.
  const announce = useCallback((message: string) => {
    setAnnounced((previous) => (previous === message ? `${message} ` : message));
  }, []);
  // Read here as well as inside the shell, and that costs nothing: `useSearchTabs` is backed by the
  // URL's own `shell` param, not by a provider, so both readers see one source of truth. What the
  // host needs from it is `openSearch`, which is also the relabel — see `onRenamed` below.
  const { openSearch } = useSearchTabs();
  const config = readRuntimeConfig();
  const indicator = resolveMarketIndicator(config);
  return (
    <>
      {/* FIRST in the tree, not last, and that is a layout constraint rather than a preference: the
          shell below is `minHeight: 100vh`, so an absolutely positioned node at its static position
          AFTER it would sit one pixel past the fold and hand every screen a scrollbar. Clipped and
          never `display: none` or `visibility: hidden` — those remove the node from the
          accessibility tree and take the announcement with it. */}
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        <Text variant="caption" color="secondary">
          {announced}
        </Text>
      </Box>
      <SearchShell
        brandLabel={indicator.text}
        brandDegraded={indicator.degraded}
        headerEnd={<UserMenu roleNames={roleNames} />}
        searchSlot={
          <HeaderSearch
            searchUsers={fetchUsersByPhone}
            searchLocations={fetchLocations}
            fetchLocationCarriers={fetchLocationCarriers}
            fetchDeliveryStates={fetchDeliveryStates}
            searchTracking={searchDeliveriesByTracking}
            onAnnounce={announce}
            inputRef={searchInputRef}
          />
        }
        renderLocation={(search) => (
          <LocationDeliveriesLayout
            search={search}
            fetchLocationDeliveries={fetchLocationDeliveries}
            searchLocations={fetchLocations}
            fetchLocationCarriers={fetchLocationCarriers}
            fetchDeliveryStates={fetchDeliveryStates}
          />
        )}
        renderUser={(search) => (
          <UserDetailCard
            search={search}
            searchUsers={fetchUsersByPhone}
            fetchDevice={fetchUserDevice}
            fetchDeliveries={fetchUserDeliveries}
            deleteUser={deleteUserAccount}
            updateUser={updateUserAccount}
            blockUser={blockUserAccount}
            unblockUser={unblockUserAccount}
            // The same read the header's address search uses, so the ficha's address combobox and
            // the header share one cache entry per term.
            searchLocations={fetchLocations}
            // The tab is labelled by the titular's name, so a correction has to relabel it or the
            // ficha and the tab above it disagree on screen. `openSearch` on a key that is already
            // open IS the relabel — the host needs nothing new for this.
            onRenamed={(name) => {
              openSearch(search.key, name);
            }}
            // The screen's one announcement region is this host's (CBX-11386). Before that lift
            // this prop had nowhere to land, and a successful save was silent for anyone who
            // cannot see the card repaint.
            onAnnounce={announce}
          />
        )}
        renderDelivery={(search, deliveryId) => (
          // The panel's gutter lives HERE, as padding on the wrapper, and not as a margin inside
          // the detail — which is where it used to be, silently doing nothing. A spaced `Stack`
          // emits `& > :not(style):not(style) { margin: 0 }`, which outspecifies an `sx` margin and
          // zeroes it even with a single child and nothing to space, so the detail rendered flush to
          // the edges of the tab while looking correctly inset in isolation. Padding cannot be
          // reset that way, and putting it on the wrapper also gives the notice below the same
          // gutter instead of letting it run to the edges.
          // `flex: 1` takes the rest of the shell's full-height column, so the two cards reach the
          // bottom gutter instead of ending with their content — and still grow past the fold with
          // it when the box log unfolds.
          <Stack spacing={2} sx={{ p: 2, flex: 1 }}>
            {/* A tab of its own means there was no titular to hang the delivery from, and an agent
                looking at a case with no ficha reads the screen as broken. So the tab says why, in
                place and in one line: `notice` and not the floating pattern, because ADR 0021 rules
                over ERRORS and this is not one — nothing failed, the account simply is not there. */}
            {search.kind === 'delivery' && (
              <Alert variant="notice" severity="info">
                {/* TODO: i18n */}
                Esta entrega no tiene titular con cuenta activa
              </Alert>
            )}
            <DeliveryLayout
              deliveryId={deliveryId}
              // The rule in one sentence: a delivery opens on the audience of the DOOR the agent came
              // through. The host is where this translation belongs — `delivery-detail` knows about
              // audiences and not about tabs.
              defaultAudience={defaultAudience(search)}
              fetchDeliveryDetail={fetchDeliveryDetail}
              confirmDeliveryCollect={confirmDeliveryCollect}
              enableDelivery={enableDelivery}
              nonCollectDelivery={nonCollectDelivery}
              rejectDelivery={rejectDelivery}
              retrieveDelivery={retrieveDelivery}
              nonRetrieveDelivery={nonRetrieveDelivery}
              extendCustody={extendCustody}
              changeDeliveryRecipient={changeDeliveryRecipient}
              // The SAME read the header search uses, handed to the detail so the dialog resolves a
              // phone through one seam and one cache — never a second definition of «Usuario found
              // by phone».
              searchUsersByPhone={fetchUsersByPhone}
              // And the header's address search, so «Notificar mismatch» places the titular's
              // address in its ubicación through the same seam and cache.
              searchLocations={fetchLocations}
              fetchLockerLog={fetchLockerLog}
              fetchBoxStatus={fetchBoxStatus}
              fetchBoxMarks={fetchBoxMarks}
              clearBoxMarks={clearBoxMarks}
              fetchLockCodes={fetchLockCodes}
              confirmLockSequence={confirmLockSequence}
              legacyBoxMaintenanceUrl={(boxId) => legacyBoxMaintenanceUrl(config, boxId)}
              reportAddressMismatch={reportAddressMismatch}
              checkAddressMismatchReport={checkAddressMismatchReport}
            />
          </Stack>
        )}
        onNewSearch={() => {
          searchInputRef.current?.focus();
        }}
      />
    </>
  );
}
